import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { type FC } from "react"

import { type ThinMembership } from "@forento/core/models/membership"
import { toPriceString } from "@forento/core/utilities/currency"
import Tooltip from "@forento/ui/components/Tooltip"
import { cn } from "@forento/ui/utilities/tailwind"

import { DragIndicatorIcon, EditIcon, UsersIcon } from "~/components/Icon"
import ZapierId from "~/components/ZapierId"

type Props = { membership: ThinMembership; onEdit(): void; onListMembers(): void }
const Membership: FC<Props> = ({ membership, onEdit, onListMembers }) => {
	const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
		id: membership.id,
	})

	const fillRatio =
		membership.maxMembers !== null && membership.maxMembers > 0
			? Math.min(membership.membersCount / membership.maxMembers, 1)
			: null

	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={cn(
				"group border-component-border bg-component-background hover:border-primary/20 relative flex flex-col overflow-hidden rounded-3xl border transition-colors duration-150",
				isDragging && "z-10 opacity-70 shadow-lg",
			)}
		>
			<div className="border-component-border flex items-start justify-between gap-3 border-b bg-white/60 pt-5 pr-2 pb-4 pl-6">
				<h2 className="flex-1 text-xl leading-tight font-semibold">{membership.title}</h2>
				<div className="flex items-center gap-0.5">
					<Tooltip tooltip="View members">
						<button
							type="button"
							onClick={onListMembers}
							className="text-light hover:bg-primary/10 hover:text-primary flex size-9 cursor-pointer items-center justify-center rounded-lg transition-colors"
						>
							<UsersIcon className="size-5" />
						</button>
					</Tooltip>
					<Tooltip tooltip="Edit membership">
						<button
							type="button"
							onClick={onEdit}
							className="text-light hover:bg-primary/10 hover:text-primary flex size-9 cursor-pointer items-center justify-center rounded-lg transition-colors"
						>
							<EditIcon className="size-5" />
						</button>
					</Tooltip>
					<Tooltip tooltip="Drag to reorder">
						<div
							{...attributes}
							{...listeners}
							className="text-light hover:bg-primary/10 hover:text-primary flex size-9 cursor-grab items-center justify-center rounded-lg transition-colors active:cursor-grabbing"
						>
							<DragIndicatorIcon className="size-5" />
						</div>
					</Tooltip>
				</div>
			</div>
			<div className="flex flex-1 flex-col gap-4 px-6 py-5">
				{membership.shortDescription && (
					<p className="text-light text-sm leading-relaxed">{membership.shortDescription}</p>
				)}
				<div className="flex flex-col gap-2">
					<div className="flex items-center justify-between gap-2 text-sm">
						<span className="text-light inline-flex items-center gap-1.5">
							<UsersIcon className="size-4" />
							Members
						</span>
						<span className="font-medium tabular-nums">
							{membership.membersCount}
							<span className="text-light">/{membership.maxMembers ?? "∞"}</span>
						</span>
					</div>
					{fillRatio !== null && (
						<div className="bg-component-border h-1.5 w-full overflow-hidden rounded-full">
							<div
								className="bg-primary h-full rounded-full transition-all duration-300"
								style={{ width: `${fillRatio * 100}%` }}
							/>
						</div>
					)}
				</div>
				<ZapierId id={membership.id} />
				<div className="flex-1" />
				<div className="border-component-border flex flex-col gap-1 border-t pt-4">
					{membership.prices.length > 0 ? (
						membership.prices.map(price => (
							<div key={price.id} className="flex items-baseline gap-1.5">
								<span className="text-xl font-semibold tracking-tight">{toPriceString(price)}</span>
								<span className="text-light text-sm">/ {price.paymentInterval}</span>
							</div>
						))
					) : (
						<span className="text-light text-sm italic">No prices set</span>
					)}
				</div>
			</div>
		</div>
	)
}

export default Membership
